import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../App';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  Building2, 
  CheckCircle2, 
  Save,
  Upload,
  ImageIcon,
  Loader2,
  Plus,
  X,
  Settings2,
  ChevronRight,
  FolderKanban,
  Target
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const Configuration = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Organization
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoUrl, setLogoUrl] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [uploadingLogo, setUploadingLogo] = useState(false);
  
  // Departments
  const [availableDepartments, setAvailableDepartments] = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [customDepartments, setCustomDepartments] = useState([]);
  const [newCustomDept, setNewCustomDept] = useState('');
  
  // Purposes
  const [purposeCategories, setPurposeCategories] = useState([]);
  const [selectedPurposes, setSelectedPurposes] = useState([]);
  const [expandedCategories, setExpandedCategories] = useState({});
  
  // Current setup info
  const [setupCompletedAt, setSetupCompletedAt] = useState(null);
  
  useEffect(() => {
    loadConfiguration();
    fetchDepartments();
    fetchPurposeCategories();
  }, []);
  
  const loadConfiguration = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/setup/configuration?user_id=${user?.id}`
      );
      
      if (response.data) {
        setOrganizationName(response.data.organization_name || '');
        setLogoUrl(response.data.organization_logo_url || '');
        setLogoPreview(response.data.organization_logo_url || null);
        setSelectedDepartments(response.data.departments || []);
        setCustomDepartments(response.data.custom_departments || []);
        setSelectedPurposes(response.data.selected_purposes || []);
        setSetupCompletedAt(response.data.setup_completed_at);
      }
    } catch (err) {
      console.error('Error loading configuration:', err);
      if (err.response?.status !== 404) {
        setError('Failed to load configuration');
      }
    } finally {
      setLoading(false);
    }
  };
  
  const fetchDepartments = async () => {
    try {
      const response = await axios.get(`${API_URL}/setup/departments`);
      setAvailableDepartments(response.data.departments || []);
    } catch (err) {
      console.error('Error fetching departments:', err);
    }
  };
  
  const fetchPurposeCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/setup/purpose-categories`);
      const categories = response.data.categories || [];
      setPurposeCategories(categories);
      const expanded = {};
      categories.forEach(cat => { expanded[cat.id] = true; });
      setExpandedCategories(expanded);
    } catch (err) {
      console.error('Error fetching purpose categories:', err);
    }
  };
  
  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp', 'image/svg+xml'];
      if (!allowedTypes.includes(file.type)) {
        setError('Please upload a valid image file (PNG, JPG, GIF, WebP, or SVG)');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError('Logo file must be less than 5MB');
        return;
      }
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
      setError('');
    }
  };
  
  const uploadLogo = async () => {
    if (!logoFile) return logoUrl;
    
    setUploadingLogo(true);
    setError('');
    
    try {
      // Get the token for authenticated requests
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication required');
      }
      
      // Determine content type from file
      const contentType = logoFile.type || 'image/png';
      
      // Get presigned URL with proper content type
      const urlResponse = await axios.post(
        `${API_URL}/setup/upload-logo?content_type=${encodeURIComponent(contentType)}`,
        {},
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      const { upload_url, logo_url } = urlResponse.data;
      
      if (!upload_url) {
        throw new Error('Failed to get upload URL from server');
      }
      
      // Upload to S3 with correct content type
      await axios.put(upload_url, logoFile, {
        headers: { 
          'Content-Type': contentType
        }
      });
      
      setLogoUrl(logo_url);
      setLogoFile(null);
      setUploadingLogo(false);
      return logo_url;
    } catch (err) {
      console.error('Error uploading logo:', err);
      setUploadingLogo(false);
      
      // Provide specific error message
      if (err.response?.status === 400) {
        setError(err.response?.data?.detail || 'Logo upload failed - S3 storage may not be configured');
      } else if (err.response?.status === 401) {
        setError('Session expired. Please log in again.');
      } else if (err.response?.status === 500) {
        setError('Server error during logo upload. Please try again later.');
      } else {
        setError(err.message || 'Failed to upload logo. Please try again.');
      }
      return logoUrl; // Return existing logo URL on error
    }
  };
  
  const toggleDepartment = (dept) => {
    setSelectedDepartments(prev => 
      prev.includes(dept) 
        ? prev.filter(d => d !== dept)
        : [...prev, dept]
    );
  };
  
  const addCustomDepartment = () => {
    if (newCustomDept.trim() && !customDepartments.includes(newCustomDept.trim())) {
      setCustomDepartments([...customDepartments, newCustomDept.trim()]);
      setNewCustomDept('');
    }
  };
  
  const removeCustomDepartment = (dept) => {
    setCustomDepartments(customDepartments.filter(d => d !== dept));
  };
  
  const togglePurpose = (purposeId) => {
    setSelectedPurposes(prev => 
      prev.includes(purposeId)
        ? prev.filter(p => p !== purposeId)
        : [...prev, purposeId]
    );
  };
  
  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };
  
  const selectAllInCategory = (category) => {
    const categoryItemIds = category.items.map(item => item.id);
    const allSelected = categoryItemIds.every(id => selectedPurposes.includes(id));
    
    if (allSelected) {
      setSelectedPurposes(prev => prev.filter(id => !categoryItemIds.includes(id)));
    } else {
      setSelectedPurposes(prev => [...new Set([...prev, ...categoryItemIds])]);
    }
  };
  
  const handleSave = async () => {
    setError('');
    setSuccess('');
    setSaving(true);
    
    try {
      let finalLogoUrl = logoUrl;
      if (logoFile) {
        finalLogoUrl = await uploadLogo();
      }
      
      await axios.put(
        `${API_URL}/setup/configuration?user_id=${user?.id}`,
        {
          organization_logo_url: finalLogoUrl,
          organization_name: organizationName,
          departments: selectedDepartments,
          custom_departments: customDepartments,
          selected_purposes: selectedPurposes
        }
      );
      
      setSuccess('Configuration saved successfully!');
      setTimeout(() => setSuccess(''), 3000);
      
    } catch (err) {
      console.error('Error saving configuration:', err);
      setError(err.response?.data?.detail || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-sky-600" />
      </div>
    );
  }
  
  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Settings2 className="h-6 w-6 text-sky-600" />
          Configuration
        </h1>
        <p className="text-gray-600">
          Manage your organization settings and data usage preferences
        </p>
        {setupCompletedAt && (
          <p className="text-sm text-gray-500 mt-1">
            Setup completed: {new Date(setupCompletedAt).toLocaleDateString()}
          </p>
        )}
      </div>
      
      {/* Alerts */}
      {error && (
        <Alert className="mb-4 border-red-200 bg-sky-50">
          <AlertDescription className="text-red-800">{error}</AlertDescription>
        </Alert>
      )}
      {success && (
        <Alert className="mb-4 border-green-200 bg-green-50">
          <AlertDescription className="text-green-800 flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4" />
            {success}
          </AlertDescription>
        </Alert>
      )}
      
      <Tabs defaultValue="organization" className="space-y-6">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="organization" className="flex items-center gap-2">
            <Building2 className="h-4 w-4" />
            Organization
          </TabsTrigger>
          <TabsTrigger value="departments" className="flex items-center gap-2">
            <FolderKanban className="h-4 w-4" />
            Departments
          </TabsTrigger>
          <TabsTrigger value="purposes" className="flex items-center gap-2">
            <Target className="h-4 w-4" />
            Data Usage
          </TabsTrigger>
        </TabsList>
        
        {/* Organization Tab */}
        <TabsContent value="organization">
          <Card>
            <CardHeader>
              <CardTitle>Organization Details</CardTitle>
              <CardDescription>
                Your organization name and logo
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="orgName">Organization Name</Label>
                <Input
                  id="orgName"
                  placeholder="e.g., City General Hospital"
                  value={organizationName}
                  onChange={(e) => setOrganizationName(e.target.value)}
                  className="max-w-md"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Organization Logo</Label>
                <div className="flex items-start gap-6">
                  <div className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50 overflow-hidden">
                    {logoPreview ? (
                      <img src={logoPreview} alt="Logo preview" className="w-full h-full object-contain" />
                    ) : (
                      <ImageIcon className="h-12 w-12 text-gray-400" />
                    )}
                  </div>
                  <div className="space-y-2">
                    <input
                      type="file"
                      id="logo-upload"
                      accept="image/*"
                      onChange={handleLogoChange}
                      className="hidden"
                    />
                    <label htmlFor="logo-upload">
                      <Button variant="outline" className="cursor-pointer" asChild>
                        <span>
                          <Upload className="h-4 w-4 mr-2" />
                          Change Logo
                        </span>
                      </Button>
                    </label>
                    <p className="text-xs text-gray-500">
                      PNG, JPG up to 5MB
                    </p>
                    {uploadingLogo && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Uploading...
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Departments Tab */}
        <TabsContent value="departments">
          <Card>
            <CardHeader>
              <CardTitle>Departments</CardTitle>
              <CardDescription>
                Select the departments you work with
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center gap-2">
                <Badge variant="secondary">
                  {selectedDepartments.length + customDepartments.length} selected
                </Badge>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-64 overflow-y-auto">
                {availableDepartments.map((dept) => (
                  <label
                    key={dept}
                    className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedDepartments.includes(dept)
                        ? 'border-sky-500 bg-sky-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Checkbox
                      checked={selectedDepartments.includes(dept)}
                      onCheckedChange={() => toggleDepartment(dept)}
                    />
                    <span className="text-sm">{dept}</span>
                  </label>
                ))}
              </div>
              
              <div className="space-y-3">
                <Label>Custom Departments</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Enter department name"
                    value={newCustomDept}
                    onChange={(e) => setNewCustomDept(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addCustomDepartment()}
                    className="max-w-xs"
                  />
                  <Button variant="outline" onClick={addCustomDepartment}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                {customDepartments.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {customDepartments.map((dept) => (
                      <Badge key={dept} variant="secondary" className="flex items-center gap-1">
                        {dept}
                        <button onClick={() => removeCustomDepartment(dept)}>
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Purposes Tab */}
        <TabsContent value="purposes">
          <Card>
            <CardHeader>
              <CardTitle>Purpose of Data Usage</CardTitle>
              <CardDescription>
                Select the purposes for which you use clinical data
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2">
                <Badge variant="secondary">
                  {selectedPurposes.length} purposes selected
                </Badge>
              </div>
              
              <div className="space-y-4 max-h-96 overflow-y-auto pr-2">
                {purposeCategories.map((category) => {
                  const categoryItemIds = category.items.map(item => item.id);
                  const selectedCount = categoryItemIds.filter(id => selectedPurposes.includes(id)).length;
                  const allSelected = selectedCount === categoryItemIds.length && categoryItemIds.length > 0;
                  
                  return (
                    <div key={category.id} className="border rounded-lg overflow-hidden">
                      <div 
                        className="flex items-center justify-between p-3 bg-gray-50 cursor-pointer hover:bg-gray-100"
                        onClick={() => toggleCategory(category.id)}
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-xl">{category.icon}</span>
                          <span className="font-medium">{category.name}</span>
                          <Badge variant="outline" className="text-xs">
                            {selectedCount}/{category.items.length}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              selectAllInCategory(category);
                            }}
                          >
                            {allSelected ? 'Deselect All' : 'Select All'}
                          </Button>
                          <ChevronRight className={`h-4 w-4 transition-transform ${expandedCategories[category.id] ? 'rotate-90' : ''}`} />
                        </div>
                      </div>
                      
                      {expandedCategories[category.id] && (
                        <div className="p-3 space-y-2">
                          {category.description && (
                            <p className="text-sm text-gray-500 mb-3">{category.description}</p>
                          )}
                          {category.items.map((item) => (
                            <label
                              key={item.id}
                              className={`flex items-center gap-2 p-2 rounded-md cursor-pointer transition-colors ${
                                selectedPurposes.includes(item.id)
                                  ? 'bg-sky-50'
                                  : 'hover:bg-gray-50'
                              }`}
                            >
                              <Checkbox
                                checked={selectedPurposes.includes(item.id)}
                                onCheckedChange={() => togglePurpose(item.id)}
                              />
                              <span className="text-sm">{item.name}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      {/* Save Button */}
      <div className="mt-6 flex justify-end">
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="bg-sky-600 hover:bg-sky-700"
        >
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Save Changes
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default Configuration;
